import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, Upload, ExternalLink, X, AlertCircle, Bot, Trash2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import toast from 'react-hot-toast';

export default function DocumentManagerModal({ open, doc, onClose, onSaved }) {
  const [activeTab, setActiveTab] = useState('msds');
  const [isUploading, setIsUploading] = useState(false);
  const [asaWarning, setAsaWarning] = useState(null);
  const fileInputRef = useRef(null);

  if (!doc) return null;

  const handleFileUploadClick = () => {
    setAsaWarning(null);
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input
    e.target.value = '';

    // Check size (> 500 KB)
    if (file.size > 500 * 1024) {
      setAsaWarning(`Halo! Asa lihat dokumen PDF kamu ukurannya terlalu besar nih (${(file.size/1024).toFixed(1)} KB). Biar server kita nggak ngos-ngosan, tolong kompres dulu ya file-nya jadi di bawah 500 KB sebelum di-upload! 🌟 (Kamu bisa pakai ilovepdf.com)`);
      return;
    }

    setIsUploading(true);
    const toastId = toast.loading('Mengunggah dokumen...');

    try {
      // 1. Upload to storage
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      if (!file_url) throw new Error("Gagal mendapatkan URL dokumen.");

      // 2. Update database
      const fieldName = `${activeTab}_url`;
      await base44.entities.PurchasingDocument.update(doc.id, {
        [fieldName]: file_url
      });

      toast.success('Dokumen berhasil diunggah!', { id: toastId });
      onSaved();
      
    } catch (error) {
      console.error(error);
      toast.error('Gagal mengunggah dokumen: ' + error.message, { id: toastId });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (tabName) => {
    if (!confirm(`Hapus dokumen ${tabName.toUpperCase()}?`)) return;
    
    setIsUploading(true);
    try {
      await base44.entities.PurchasingDocument.update(doc.id, {
        [`${tabName}_url`]: null
      });
      toast.success('Dokumen dihapus.');
      onSaved();
    } catch (e) {
      toast.error('Gagal menghapus dokumen.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!confirm(`Hapus folder dokumen ${doc.material_name}? Semua file PDF di dalamnya juga akan terhapus.`)) return;
    
    setIsUploading(true);
    try {
      await base44.entities.PurchasingDocument.hardDelete(doc.id);
      toast.success('Folder berhasil dihapus.');
      onClose();
      onSaved();
    } catch (e) {
      toast.error('Gagal menghapus folder.');
    } finally {
      setIsUploading(false);
    }
  };

  const tabsConfig = [
    { id: 'msds', label: 'MSDS' },
    { id: 'coa', label: 'COA' },
    { id: 'kipra', label: 'KIPRA' },
    { id: 'halal', label: 'HALAL' }
  ];

  const currentUrl = doc[`${activeTab}_url`];

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl bg-[#FFFDF9] border-[#E6D5B8] p-0 overflow-hidden">
        <DialogHeader className="p-6 border-b border-[#E6D5B8]/40 bg-white flex flex-row items-start justify-between gap-4">
          <DialogTitle className="text-xl font-black text-[#302621] flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F9F5EC] text-[#8C6208] flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div className="flex flex-col">
              <span>{doc.material_name}</span>
              <span className="text-xs font-semibold text-[#A39171] uppercase tracking-widest mt-0.5">{doc.vendor_name}</span>
            </div>
          </DialogTitle>
          <Button 
            variant="ghost" 
            onClick={handleDeleteFolder} 
            disabled={isUploading} 
            className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-xl gap-2 mt-[-5px]"
          >
            <Trash2 className="w-4 h-4" />
            <span className="text-xs font-bold hidden sm:inline">Hapus Folder</span>
          </Button>
        </DialogHeader>

        <div className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-4 w-full bg-slate-100 p-1 rounded-xl">
              {tabsConfig.map(tab => (
                <TabsTrigger 
                  key={tab.id} 
                  value={tab.id}
                  className="rounded-lg text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[#8C6208] data-[state=active]:shadow-sm"
                >
                  {tab.label}
                  {doc[`${tab.id}_url`] && <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 ml-1.5"></div>}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="mt-6 min-h-[300px]">
              
              {/* ASA WARNING POPUP */}
              {asaWarning && (
                <div className="mb-6 bg-rose-50 border border-rose-200 rounded-2xl p-4 flex gap-4 items-start animate-in zoom-in-95 duration-300 shadow-sm relative">
                  <button onClick={() => setAsaWarning(null)} className="absolute top-2 right-2 p-1 text-rose-400 hover:text-rose-600 rounded-lg hover:bg-rose-100/50">
                    <X className="w-4 h-4" />
                  </button>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center shrink-0 border-2 border-white shadow-sm">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-black text-rose-800 text-sm mb-1">Asa the AI Assistant</h4>
                    <p className="text-xs text-rose-700 leading-relaxed font-medium">{asaWarning}</p>
                    <a href="https://www.ilovepdf.com/compress_pdf" target="_blank" rel="noreferrer" className="inline-block mt-3 px-3 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-800 text-[10px] font-bold rounded-lg transition-colors border border-rose-200">
                      Buka iLovePDF.com
                    </a>
                  </div>
                </div>
              )}

              {/* DOCUMENT VIEWER / UPLOADER */}
              {currentUrl ? (
                <div className="h-[300px] bg-slate-50 border border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-4 animate-in fade-in relative">
                  <div className="w-16 h-16 bg-white border border-slate-200 shadow-sm rounded-2xl flex items-center justify-center text-emerald-600">
                    <FileText className="w-8 h-8" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-slate-700">Dokumen {activeTab.toUpperCase()} Tersedia</p>
                    <p className="text-xs text-slate-500 mt-1">Anda dapat melihat, mengganti, atau menghapus dokumen ini.</p>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                    <Button onClick={() => window.open(currentUrl, '_blank')} className="gap-2 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-xl h-10 font-bold px-4">
                      <ExternalLink className="w-4 h-4" /> Buka
                    </Button>
                    <Button variant="outline" onClick={handleFileUploadClick} disabled={isUploading} className="gap-2 border-[#E6D5B8] text-[#8C6208] hover:bg-white rounded-xl h-10 font-bold px-4">
                      <Upload className="w-4 h-4" /> Ganti File
                    </Button>
                    <Button variant="ghost" onClick={() => handleDelete(activeTab)} disabled={isUploading} className="w-10 h-10 p-0 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-xl">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="h-[300px] border-2 border-dashed border-slate-200 hover:border-[#8C6208]/50 bg-slate-50 hover:bg-[#F9F5EC]/50 rounded-2xl flex flex-col items-center justify-center gap-4 transition-all animate-in fade-in group">
                  <div className="w-16 h-16 bg-white border border-slate-200 shadow-sm rounded-full flex items-center justify-center text-slate-400 group-hover:text-[#8C6208] transition-colors">
                    <Upload className="w-6 h-6" />
                  </div>
                  <div className="text-center px-4">
                    <p className="text-sm font-bold text-slate-700 group-hover:text-[#302621]">Belum ada {activeTab.toUpperCase()}</p>
                    <p className="text-xs text-slate-500 mt-1 max-w-[250px]">Upload dokumen PDF berukuran maksimal 500 KB agar server tetap ringan.</p>
                  </div>
                  <Button 
                    onClick={handleFileUploadClick} 
                    disabled={isUploading}
                    variant="outline"
                    className="mt-2 gap-2 border-[#E6D5B8] text-[#8C6208] hover:bg-white rounded-xl font-bold"
                  >
                    {isUploading ? 'Mengunggah...' : `Upload PDF ${activeTab.toUpperCase()}`}
                  </Button>
                </div>
              )}

              {/* HIDDEN FILE INPUT */}
              <input 
                type="file" 
                ref={fileInputRef} 
                accept="application/pdf"
                className="hidden" 
                onChange={handleFileChange}
              />
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
